import { useEffect, type RefObject } from 'react'

// Landing hero effect: faint "futures" stream from the left into the example
// card, and the card fills one square per future in the example's real
// win/loss split (HOMEPAGE_EXAMPLE.heroResult, kept honest by `npm run
// hero:check`). The paths themselves are illustrative random walks, not engine
// runs — the page labels them so. One rAF loop drives both canvases; it plays
// once when the hero is on screen, then stops. Reduced motion draws the
// finished picture with no animation. Both canvases are decorative
// (aria-hidden); every figure is also in the card as text.

const N_PATHS = 260
const YEARS = 20
const COLS = 50
const ROWS = 20
const N_FUTURES = COLS * ROWS
const DURATION_MS = 4200

function mulberry(seed: number) {
  let a = seed
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
function gauss(r: () => number) {
  let u = 0, v = 0
  while (!u) u = r()
  while (!v) v = r()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}
function shuffle<T>(a: T[], r: () => number) {
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]] }
  return a
}
function fit(c: HTMLCanvasElement) {
  const d = Math.min(2, window.devicePixelRatio || 1)
  const w = c.clientWidth, h = c.clientHeight
  if (c.width !== Math.round(w * d) || c.height !== Math.round(h * d)) { c.width = Math.round(w * d); c.height = Math.round(h * d) }
  const x = c.getContext('2d')
  x?.setTransform(d, 0, 0, d, 0, 0)
  return { x, w, h }
}
const themeRgb = (name: string) =>
  `rgb(${getComputedStyle(document.documentElement).getPropertyValue(name).trim()})`

export interface HeroFuturesProps {
  heroRef: RefObject<HTMLElement>
  cardRef: RefObject<HTMLElement>
  waffleRef: RefObject<HTMLCanvasElement>
  counterRef: RefObject<HTMLElement>
  /** The example's win share, e.g. "58%", straight from heroResult.pct. */
  winPct: string
}

export default function HeroFutures({ heroRef, cardRef, waffleRef, counterRef, winPct }: HeroFuturesProps) {
  useEffect(() => {
    const hero = heroRef.current, card = cardRef.current
    const canvas = hero?.querySelector<HTMLCanvasElement>('canvas[data-hero-futures]')
    if (!hero || !card || !canvas) return

    // Paths: random walks whose drift makes "Keep working ahead" the more
    // common ending, as in the example. Illustrative shape only.
    const r = mulberry(42)
    const mu = 0.2 / Math.sqrt(YEARS)
    const paths = Array.from({ length: N_PATHS }, () => {
      let y = 0; const p = [0]
      for (let i = 0; i < YEARS; i++) { y += mu + gauss(r); p.push(y) }
      return p
    })
    const span = 3.2 * Math.sqrt(YEARS)

    // Waffle: exactly the example's split, filled in random order.
    const wins = Math.round((parseFloat(winPct) || 0) / 100 * N_FUTURES)
    const outcomes = shuffle(Array.from({ length: N_FUTURES }, (_, k) => k < wins), r)
    const winCells: number[] = [], lossCells: number[] = []
    for (let col = 0; col < COLS; col++) for (let row = 0; row < ROWS; row++)
      (col * ROWS + row < wins ? winCells : lossCells).push(row * COLS + col)
    let wi = 0, li = 0
    const seq = outcomes.map((o) => (o ? winCells[wi++] : lossCells[li++]))

    const drawPaths = (t: number) => {
      const { x, w, h } = fit(canvas)
      if (!x) return 1
      x.clearRect(0, 0, w, h)
      const hr = hero.getBoundingClientRect(), cr = card.getBoundingClientRect()
      const stacked = cr.top - hr.top > hr.height * 0.4
      const ex = stacked ? cr.left - hr.left + cr.width / 2 : cr.left - hr.left + 4
      const cy = stacked ? cr.top - hr.top - 4 : cr.top - hr.top + cr.height / 2
      const amp = stacked ? 70 : Math.min(h * 0.46, 230)
      const prim = themeRgb('--c-primary'), sec = themeRgb('--c-secondary')
      const pos = (p: number[], kk: number): [number, number] => {
        const k0 = Math.min(YEARS - 1, Math.floor(kk)), f = kk - k0
        const yv = p[k0] + (p[k0 + 1] - p[k0]) * f, u = kk / YEARS
        // open out from one point "today", converge into the card at the end
        const env = (0.2 + 0.8 * Math.pow(u, 0.6)) * (u > 0.88 ? Math.max(0.08, 1 - (u - 0.88) * 7.5) : 1)
        return [ex * u, cy - (yv / span) * amp * env]
      }
      let landed = 0
      x.lineWidth = 1.25
      for (let i = 0; i < N_PATHS; i++) {
        const st = (i / N_PATHS) * 0.62
        const prog = Math.max(0, Math.min(1, (t - st) / 0.36))
        if (prog <= 0) continue
        if (prog >= 1) landed++
        // Stacked (phone) layout: no room for the paths beside the text, so
        // only the card's squares animate.
        if (stacked) continue
        const p = paths[i], last = prog * YEARS
        x.strokeStyle = x.fillStyle = p[YEARS] > 0 ? prim : sec
        x.beginPath()
        for (let k = 0; k <= Math.ceil(last); k++) {
          const [px, py] = pos(p, Math.min(k, last))
          if (k === 0) x.moveTo(px, py); else x.lineTo(px, py)
        }
        x.globalAlpha = 0.22
        x.stroke()
        if (prog < 1) { const [px, py] = pos(p, last); x.globalAlpha = 0.9; x.fillRect(px - 1.5, py - 1.5, 3, 3) }
      }
      x.globalAlpha = 1
      return landed / N_PATHS
    }

    const drawWaffle = (frac: number) => {
      const c = waffleRef.current
      const n = Math.round(frac * N_FUTURES)
      if (counterRef.current) counterRef.current.textContent = n.toLocaleString('en-US')
      if (!c) return
      const { x, w, h } = fit(c)
      if (!x) return
      x.clearRect(0, 0, w, h)
      const cw = w / COLS, ch = h / ROWS
      for (let k = 0; k < n; k++) {
        const idx = seq[k]
        x.fillStyle = outcomes[k] ? 'rgba(255,255,255,0.95)' : 'rgba(20,10,5,0.45)'
        x.fillRect((idx % COLS) * cw + 0.75, Math.floor(idx / COLS) * ch + 0.75, cw - 1.5, ch - 1.5)
      }
    }

    const draw = (t: number) => drawWaffle(drawPaths(t))

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    let raf = 0, t0: number | null = null, elapsed = 0, visible = false, done = false
    const frame = (now: number) => {
      if (t0 === null) t0 = now - elapsed
      elapsed = now - t0
      const t = Math.min(1, elapsed / DURATION_MS)
      draw(t)
      if (t >= 1) { done = true; return }
      if (visible) raf = requestAnimationFrame(frame)
      else t0 = null // pause; resume from `elapsed`
    }
    const play = () => {
      if (done) return
      if (reduce.matches) { draw(1); done = true; return }
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(frame)
    }
    // Complete at rest (thumbnails, no-JS-timing crawlers, before scroll-in).
    draw(reduce.matches ? 1 : 0)
    if (reduce.matches) done = true

    const io = new IntersectionObserver(([e]) => {
      const was = visible
      visible = e.isIntersecting
      if (visible && !was) play()
    }, { threshold: 0.2 })
    io.observe(hero)

    const redraw = () => { if (done) draw(1) }
    let rz = 0
    const onResize = () => { window.clearTimeout(rz); rz = window.setTimeout(redraw, 120) }
    window.addEventListener('resize', onResize)
    const mo = new MutationObserver(redraw)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    return () => {
      cancelAnimationFrame(raf)
      io.disconnect()
      mo.disconnect()
      window.removeEventListener('resize', onResize)
      window.clearTimeout(rz)
    }
  }, [heroRef, cardRef, waffleRef, counterRef, winPct])

  return (
    <canvas
      data-hero-futures
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none z-0"
      style={{
        // Lines stay faint on the headline side so text contrast is unchanged.
        WebkitMaskImage: 'linear-gradient(90deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 45%, #000 70%)',
        maskImage: 'linear-gradient(90deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 45%, #000 70%)',
      }}
    />
  )
}
